import { useIntl } from "react-intl";
import { EquipmentInfoResponseType } from "../../../types/connected-object";
import { getEquipmentSiteName } from "../../../utils/equipment";
import LinkIcon from "../../../components/icons/Link";
import CopyToClipboard from "../../../components/ui/CopyToClipboard";

type EquipmentInfoProps = {
  data: EquipmentInfoResponseType;
};

type FieldRowProps = {
  label: string;
  value: string | null;
  copyable?: boolean;
};

const FieldRow = ({ label, value, copyable }: FieldRowProps) => {
  if (!value) return null;

  return (
    <p className="text-[16px] leading-[21px] text-[#5f6f86] text-left flex items-center justify-start gap-2 flex-wrap min-[501px]:justify-center min-[501px]:text-center">
      <span>
        {label} : <span className="font-semibold text-[#43536e]">{value}</span>
      </span>
      {copyable && <CopyToClipboard text={value} />}
    </p>
  );
};

const LinkButton = ({ href, label, blue }: { href: string; label: string; blue?: boolean }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`w-full py-3 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-3 ${
        blue ? "bg-[#0066B1]" : "bg-[#F9A000]"
      }`}
    >
      <LinkIcon className="shrink-0" />
      {label}
    </a>
  );
};

const EquipmentInfo = ({ data }: EquipmentInfoProps) => {
  const intl = useIntl();
  const { id, equipment } = data;
  const {
    building,
    designation,
    elementType,
    location,
    repere,
    SapPosteTechnique,
    SapEquipement,
    ealicoLink,
    ealicoLinkTitle,
    ealicoLink2,
    ealicoLink2Title,
    otherLink1Title,
    otherLink1Url,
    otherLink2Title,
    otherLink2Url
  } = equipment;
  const siteName = getEquipmentSiteName(equipment);

  const ealico1Label =
    ealicoLinkTitle?.trim() || intl.formatMessage({ id: "equipment.info.ealico" });
  const ealico2Label =
    ealicoLink2Title?.trim() || intl.formatMessage({ id: "equipment.info.ealico2" });
  const other1Label =
    otherLink1Title?.trim() || intl.formatMessage({ id: "equipment.info.other1Default" });
  const other2Label =
    otherLink2Title?.trim() || intl.formatMessage({ id: "equipment.info.other2Default" });

  return (
    <div className="min-h-dvh bg-[#f2f2f2] flex justify-center sm:py-8 sm:px-4">
      <div className="w-full sm:max-w-[620px] bg-white shadow-sm overflow-hidden sm:rounded-[40px]">

        {/* Blue header — fixed 150 px, logo overlaps the boundary */}
        <div className="relative bg-[#0066B1] h-[150px] rounded-b-[42px] flex flex-col items-center justify-between py-5">
          <p className="text-[#ffffff33] text-7xl font-semibold leading-none">Tereos</p>
          <img
            className="w-[120px] h-[120px] object-contain -mb-[60px]"
            src="/tereos-white-landing.png"
            alt={intl.formatMessage({ id: "common.tereosAlt" })}
          />
        </div>

        {/* Info section — full width, top padding accounts for the overlapping logo (60 px + gap) */}
        <div className="w-full px-6 pt-[76px] pb-9">
          <h1 className="text-center text-[44px] leading-[50px] font-bold text-[#183659]">
            {intl.formatMessage({ id: "equipment.info.idHeading" }, { id })}
          </h1>

          <div className="mt-8 flex flex-col gap-1 min-[501px]:items-center">
            <FieldRow label={intl.formatMessage({ id: "equipment.info.site" })} value={siteName} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.building" })} value={building} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.designation" })} value={designation} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.elementType" })} value={elementType} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.location" })} value={location} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.repere" })} value={repere} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.SapPosteTechnique" })} value={SapPosteTechnique} />
            <FieldRow label={intl.formatMessage({ id: "equipment.info.SapEquipement" })} value={SapEquipement} copyable />
          </div>
        </div>

        {/* Buttons section — full width, capped at 220 px on narrow screens */}
        <div className="w-full bg-[#ececec] px-6 py-8 flex justify-center">
          <div className="flex flex-col gap-4 w-full max-[500px]:w-[220px] min-[500px]:max-w-[360px]">
            {ealicoLink && (
              <LinkButton
                href={ealicoLink}
                label={ealico1Label}
                blue
              />
            )}
            {ealicoLink2 && (
              <LinkButton
                href={ealicoLink2}
                label={ealico2Label}
                blue
              />
            )}
            {otherLink1Url && <LinkButton href={otherLink1Url} label={other1Label} />}
            {otherLink2Url && <LinkButton href={otherLink2Url} label={other2Label} />}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EquipmentInfo;
